import { FatalDecoratorLimitDescriptorError } from '@rehaje/reactive'
import {
  createMemberDecorator,
  ensureProtoProps,
  isInheritProto
} from '@rehaje/shared'

import { TypedNode } from '../models/TypedNode'

import type { MixtureMemberDecorator } from '@rehaje/shared'

/**
 * 从 ADT 协议中同步属性的 TS 装饰器，只能用于装饰 TypedNode 子类属性
 * @example
 * // jsdoc 限制，实际使用请将 * 替换为 @
 * class MyNode extends TypedNode {
 *   *fromSpecific()
 *   *reactive()
 *   test = 123
 * }
 * @param from 属性名
 */
export const fromSpecific: () => MixtureMemberDecorator = createMemberDecorator(
  {
    tsDecorator(proto: TypedNode, key, descriptor) {
      if (!isInheritProto(proto, TypedNode.prototype)) {
        throw new FatalDecoratorLimitDescriptorError(
          'fromSpecific',
          key,
          'TypedNode'
        )
      }

      const fromKeys = ensureProtoProps(proto, '__r_from_keys', [])
      if (!fromKeys.includes(key)) fromKeys.push(key)
      const fromMaps = ensureProtoProps(proto, '__r_from_maps', {})
      fromMaps[key] = true

      const fromSpecificKey = `__r_from_specific_${key}`
      const assignedKey = `__r_assigned_${key}`

      if (typeof descriptor?.value === 'function') {
        return descriptor
      }

      const get =
        descriptor?.get ??
        new Function(
          `
        return this.${fromSpecificKey}
        `
        )
      const set =
        descriptor?.set ??
        new Function(
          'value',
          `
        this.${fromSpecificKey} = value
        `
        )

      return {
        ...descriptor,
        get,
        set: new Function(
          'set',
          `
        return function(value){
          if (!this.__r_initialized) {
            if (this.${assignedKey}) return
            this.${assignedKey} = true
          }
          set.call(this, value)
        }
        `
        )(set)
      } as PropertyDescriptor
    }
  }
)
